import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton } from "@/components/ui-kit";
import { Plus } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";

const empty = { name: "", category: "lubricant", unit: "litre", unit_cost: 0, quantity: 0, min_stock: 0 };

export default function Consumables() {
    const [list, setList] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [logs, setLogs] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [tab, setTab] = useState("items");
    const [logOpen, setLogOpen] = useState(false);
    const [logForm, setLogForm] = useState({ consumable_id: "", vehicle_id: "", quantity: 1, odometer: 0, notes: "" });

    const load = () => api.get("/consumables").then((r) => setList(r.data));
    useEffect(() => {
        load();
        api.get("/vehicles").then((r) => setVehicles(r.data));
        api.get("/consumables/logs").then((r) => setLogs(r.data)).catch(() => {});
    }, []);

    const save = async () => {
        try {
            const payload = { ...form, unit_cost: Number(form.unit_cost), quantity: Number(form.quantity), min_stock: Number(form.min_stock) };
            if (editing) await api.put(`/consumables/${editing}`, payload);
            else await api.post("/consumables", payload);
            toast.success("Saved"); setOpen(false); setEditing(null); setForm(empty); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const edit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); setOpen(true); };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/consumables/${id}`); load(); };

    const submitLog = async () => {
        try {
            await api.post("/consumables/log", { ...logForm, quantity: Number(logForm.quantity), odometer: Number(logForm.odometer) || 0 });
            toast.success("Consumption logged");
            setLogOpen(false); setLogForm({ consumable_id: "", vehicle_id: "", quantity: 1, odometer: 0, notes: "" });
            load();
            api.get("/consumables/logs").then((r) => setLogs(r.data));
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const vehMap = Object.fromEntries(vehicles.map((v) => [v.id, v]));

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="FLEET STORES"
                title="Consumables"
                subtitle="Lubricants, oils, coolants — track stock & consumption per vehicle."
                action={
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={() => setLogOpen(true)} data-testid="log-consumption-btn"><Plus size={14} /> Log consumption</Button>
                        <NewButton onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} label="Add item" testid="add-consumable-btn" />
                    </div>
                }
            />

            <div className="flex gap-1 border-b border-fleet-border mb-6">
                {[["items", "Items"], ["logs", "Consumption Logs"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                        className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-fleet-ink text-fleet-ink font-medium" : "border-transparent text-fleet-inkSoft hover:text-fleet-ink"}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === "items" && (
                <Surface className="overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50"><tr className="text-left">
                            {["NAME", "CATEGORY", "UNIT", "QUANTITY", "MIN", "UNIT COST", "STATUS", ""].map((h) => <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {list.map((c) => {
                                const low = c.quantity <= c.min_stock;
                                return (
                                    <tr key={c.id} className="border-t border-fleet-border data-grid-row">
                                        <td className="px-5 py-3 font-medium">{c.name}</td>
                                        <td className="px-5 py-3 capitalize">{c.category}</td>
                                        <td className="px-5 py-3">{c.unit}</td>
                                        <td className="px-5 py-3 font-mono">{c.quantity}</td>
                                        <td className="px-5 py-3 font-mono text-fleet-inkSoft">{c.min_stock}</td>
                                        <td className="px-5 py-3 font-mono">₹{(c.unit_cost || 0).toLocaleString("en-IN")}</td>
                                        <td className="px-5 py-3">{low ? <Badge variant="critical">LOW</Badge> : <Badge variant="healthy">OK</Badge>}</td>
                                        <td className="px-5 py-3 text-right">
                                            <Button variant="ghost" size="sm" onClick={() => edit(c)} data-testid={`edit-cons-${c.id}`}>Edit</Button>
                                            <Button variant="ghost" size="sm" onClick={() => del(c.id)} data-testid={`del-cons-${c.id}`}>Del</Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {list.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">No items yet.</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "logs" && (
                <Surface className="overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50"><tr className="text-left">
                            {["DATE", "ITEM", "VEHICLE", "QTY", "COST", "ODO", "NOTES"].map((h) => <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {logs.map((l) => (
                                <tr key={l.id} className="border-t border-fleet-border">
                                    <td className="px-5 py-3 font-mono">{formatDate(l.created_at)}</td>
                                    <td className="px-5 py-3">{l.consumable_name}</td>
                                    <td className="px-5 py-3 font-mono text-xs">{vehMap[l.vehicle_id]?.reg_number || "—"}</td>
                                    <td className="px-5 py-3 font-mono">{l.quantity}</td>
                                    <td className="px-5 py-3 font-mono">₹{(l.total_cost || 0).toLocaleString("en-IN")}</td>
                                    <td className="px-5 py-3 font-mono">{l.odometer || "—"}</td>
                                    <td className="px-5 py-3 text-fleet-inkSoft">{l.notes || ""}</td>
                                </tr>
                            ))}
                            {logs.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">No logs yet.</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit consumable" : "Add consumable"}
                footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></>}>
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Engine oil 15W-40" />
                    <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} options={["lubricant", "oil", "coolant", "grease", "other"]} />
                    <Input label="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="litre, kg" />
                    <Input label="Unit cost (₹)" type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
                    <Input label="Quantity" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                    <Input label="Min stock" type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} />
                </div>
            </Modal>

            <Modal open={logOpen} onClose={() => setLogOpen(false)} title="Log consumption"
                footer={<><Button variant="secondary" onClick={() => setLogOpen(false)}>Cancel</Button><Button onClick={submitLog} data-testid="save-log">Log</Button></>}>
                <div className="grid grid-cols-2 gap-4">
                    <Select label="Item" value={logForm.consumable_id} onChange={(e) => setLogForm({ ...logForm, consumable_id: e.target.value })}
                        options={[{ value: "", label: "Select…" }, ...list.map((c) => ({ value: c.id, label: `${c.name} (${c.quantity} ${c.unit})` }))]} />
                    <Select label="Vehicle" value={logForm.vehicle_id} onChange={(e) => setLogForm({ ...logForm, vehicle_id: e.target.value })}
                        options={[{ value: "", label: "Select…" }, ...vehicles.map((v) => ({ value: v.id, label: v.reg_number }))]} />
                    <Input label="Quantity" type="number" step="0.1" value={logForm.quantity} onChange={(e) => setLogForm({ ...logForm, quantity: e.target.value })} />
                    <Input label="Odometer" type="number" value={logForm.odometer} onChange={(e) => setLogForm({ ...logForm, odometer: e.target.value })} />
                    <Input label="Notes" className="col-span-2" value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} />
                </div>
            </Modal>
        </div>
    );
}
